import { Injectable } from '@angular/core';
import { BaseNotifier } from './base-notifier';
import { Ref } from '../providers/ref';
import { DarkModeRepositoryProvider } from '../../shared/providers/repository-providers';

@Injectable({ providedIn: 'root' })
export class DarkModeNotifier extends BaseNotifier<boolean> {
  private static readonly INITIAL = false;

  constructor(ref: Ref) {
    super(ref, DarkModeNotifier.INITIAL);
  }

  private get repo() {
    return this.ref.watch(DarkModeRepositoryProvider).instance;
  }

  override async build(): Promise<boolean> {
    await this.restoreState();
    // El tema persistido se aplica de inmediato (sin diferir al siguiente
    // frame): sin esta restauración, tras recargar la página el estado era
    // oscuro pero html/body seguían en claro y el primer pintado salía claro.
    this.applyToDocument(false);
    return this.state();
  }

  async toggle(): Promise<void> {
    const isDark = !this.state();
    this.setState(isDark);
    this.applyToDocument();
    await this.repo.save(isDark);
  }

  async setDarkMode(isDark: boolean): Promise<void> {
    this.setState(isDark);
    this.applyToDocument();
    await this.repo.save(isDark);
  }

  private async restoreState(): Promise<void> {
    const stored = await this.repo.load();
    if (stored.success && stored.data !== undefined) {
      this.setState(stored.data);
    }
  }

  /**
   * @param deferred `true` (por defecto) espera al siguiente frame para que la
   *   clase llegue con el DOM listo; la restauración inicial usa `false` para
   *   que el primer pintado ya salga con el tema guardado.
   */
  private applyToDocument(deferred = true): void {
    if (deferred) {
      requestAnimationFrame(() => this.applyTheme());
      return;
    }
    this.applyTheme();
  }

  private applyTheme(): void {
    if (this.state()) {
      document.body.classList.add('dark-mode');
      document.documentElement.classList.add('dark-mode');
      document.documentElement.classList.add('p-dark');
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.classList.remove('p-dark');
    }
  }
}
